import React, { useMemo } from 'react';
import Card from '../../../../muiComponents/Card';
import {
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
} from '@mui/material';
import StatusCell from '../../../../Table/StatusCell';
import TableHeader from '../../../../Table/TableHeader';
import '../../../../stylesheets/stylesComponents/table.scss';
import useTranslate from '../../../../customHooks/useTranslate';
import { useConnectionRequests } from '../../../contextsProperty/ContextConnectionRequests';
import {
    formatDate,
    formatDateTime,
    isMinValueDate,
} from '../../../../utils/formatDates';
import useLanguage from '../../../../customHooks/useLanguage';
import { ConnetionRequestSource } from '../../../../types/zinantis/connectionRequests/connectionRequestTypes';
import CancelRequestButton from '../requestButtons/CancelRequestButton';
import AcceptConnectionButton from '../requestButtons/AcceptConnectionButton';
import RejectConnectionButton from '../requestButtons/RejectConexionButton';
import {
    PENDING_REQUEST_STATUS_KEY,
    requestsListColumns,
} from '../../../../utils/zinantis/requests/requestsUtils';
import Tooltip from '../../../../muiComponents/Tooltip';
import useTimeZone from '../../../../customHooks/useTimeZone';
import { useZinantisMembers } from '../../../contextsProperty/ContextZinantisMembers';
import { Link } from 'react-router-dom';
import encodeBase64 from '../../../../utils/encodeBase64';
import routes from '../../../../utils/commonVariables/routes';

const ZinantisRequestsList = () => {
    const translate = useTranslate();
    const language = useLanguage();
    const timeZone = useTimeZone();
    const {
        items,
        sortedBy,
        sortDir,
        sortByField,
        isLoading,
        handleScroll,
        handleLoading,
        requestedBy,
        handleSearch,
    } = useConnectionRequests();
    const { handleOwnerZinantisData } = useZinantisMembers();

    const columns = useMemo(
        () => requestsListColumns(requestedBy),
        [requestedBy]
    );

    return (
        <Card className={`flex-column flex-grow min-height-0 `}>
            <TableContainer
                className=' flex-grow min-height-0 overflow-auto'
                onScroll={handleScroll}
            >
                <Table stickyHeader size='small'>
                    <TableHeader
                        headerColumns={columns}
                        sortedBy={sortedBy}
                        sortDir={sortDir}
                        sortByField={sortByField}
                        isLoading={isLoading}
                        selectable={false}
                    />
                    <TableBody>
                        {items.map(item => (
                            <TableRow key={item.Id}>
                                <TableCell>
                                    <StatusCell
                                        text={item.Status}
                                        color={item.StatusColor}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Link
                                        className=' link-onhover'
                                        to={`${
                                            routes.landingPrivate +
                                            routes.zinantisSection +
                                            routes.zinantisMembers
                                        }?id=${encodeBase64(item.IdAgency)}`}
                                    >
                                        <Tooltip titleText='navigate_to_member_detail'>
                                            <h3 className='font-weight-600 text-secondary-700 width-content'>
                                                {item.Agency}
                                            </h3>
                                        </Tooltip>
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    <Tooltip
                                        title={formatDateTime(
                                            item.RequestDate,
                                            language,
                                            timeZone
                                        )}
                                    >
                                        {formatDate(item.RequestDate, language)}
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    {isMinValueDate(item.ResolutionDate) ? (
                                        formatDate(
                                            item.ResolutionDate,
                                            language
                                        )
                                    ) : (
                                        <Tooltip
                                            title={formatDateTime(
                                                item.ResolutionDate,
                                                language,
                                                timeZone
                                            )}
                                        >
                                            {formatDate(
                                                item.ResolutionDate,
                                                language
                                            )}
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div className='width100perc height100perc flex gap-10'>
                                        {requestedBy ===
                                            ConnetionRequestSource.OWNER &&
                                            item.StatusKey ===
                                                PENDING_REQUEST_STATUS_KEY && (
                                                <CancelRequestButton
                                                    idRequest={item.Id}
                                                    onChangeLoading={
                                                        handleLoading
                                                    }
                                                    onRequestSuccess={async () => {
                                                        handleSearch();
                                                        handleOwnerZinantisData();
                                                    }}
                                                />
                                            )}
                                        {requestedBy ===
                                            ConnetionRequestSource.AGENCY &&
                                            item.StatusKey ===
                                                PENDING_REQUEST_STATUS_KEY && (
                                                <RejectConnectionButton
                                                    idRequest={item.Id}
                                                    onChangeLoading={
                                                        handleLoading
                                                    }
                                                    onRequestSuccess={async () => {
                                                        handleSearch();
                                                        handleOwnerZinantisData();
                                                    }}
                                                />
                                            )}
                                        {requestedBy ===
                                            ConnetionRequestSource.AGENCY &&
                                            item.StatusKey ===
                                                PENDING_REQUEST_STATUS_KEY && (
                                                <AcceptConnectionButton
                                                    idRequest={item.Id}
                                                    onChangeLoading={
                                                        handleLoading
                                                    }
                                                    onRequestSuccess={async () => {
                                                        handleSearch();
                                                        handleOwnerZinantisData();
                                                    }}
                                                />
                                            )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    {(items.length === 0 || isLoading) && (
                        <caption className='no-results'>
                            {isLoading && <LinearProgress />}
                            {items.length === 0 &&
                                !isLoading &&
                                translate('no_results')}
                        </caption>
                    )}
                </Table>
            </TableContainer>
        </Card>
    );
};

export default ZinantisRequestsList;
